import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { useVirtualizer } from "@tanstack/react-virtual";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { smartShuffle, markSeenBatch, randomStartIndex } from "@/lib/feedShuffle";

/**
 * Doomscroll feed — fullscreen, position:fixed approach.
 *
 * Smart shuffle: unseen items first, newer items prioritized,
 * session-random ordering. Logo click triggers re-shuffle.
 */
export default function Feed({ filters, onOpenIntent, dockStyle = "commerce", shuffleKey = 0 }) {
  const parentRef = useRef(null);
  const queryClient = useQueryClient();
  const [startOffset, setStartOffset] = useState(0);

  const { data, fetchNextPage, hasNextPage, isLoading, isFetchingNextPage } = useInfiniteQuery({
    queryKey: ["feed", filters],
    initialPageParam: 0,
    queryFn: async ({ pageParam = 0 }) => {
      const params = { page: pageParam, page_size: 8, ...filters };
      Object.keys(params).forEach(
        (k) => (params[k] === "" || params[k] == null) && delete params[k]
      );
      const r = await api.get("/feed", { params });
      return r.data;
    },
    getNextPageParam: (last) => (last.has_more ? last.page + 1 : undefined),
  });

  // Raw items from API
  const rawItems = useMemo(
    () => data?.pages.flatMap((p) => p.items) || [],
    [data]
  );

  // Apply smart shuffle — re-shuffle when shuffleKey changes (logo click)
  const realItems = useMemo(() => {
    if (!rawItems.length) return [];
    return smartShuffle(rawItems, { seed: shuffleKey * 7919 + Date.now() });
  }, [rawItems, shuffleKey]);

  // Pick random start index when items or shuffleKey changes
  useEffect(() => {
    if (realItems.length) {
      setStartOffset(randomStartIndex(realItems.length));
    }
  }, [shuffleKey, realItems.length]);

  // Mark visible items as seen
  const lastMarkedRef = useRef("");
  useEffect(() => {
    if (!realItems.length) return;
    const visibleIds = realItems.slice(0, 8).map((i) => i.id).filter(Boolean);
    const key = visibleIds.join(",");
    if (key !== lastMarkedRef.current) {
      lastMarkedRef.current = key;
      markSeenBatch(visibleIds);
    }
  }, [realItems]);

  const [loopCount, setLoopCount] = useState(1);

  // Reset loop when filters or shuffle change
  useEffect(() => { setLoopCount(1); }, [filters, shuffleKey]);

  // For looping: offset the start so user doesn't always begin at index 0
  const offsetItems = useMemo(() => {
    if (!realItems.length) return realItems;
    const n = realItems.length;
    const start = startOffset % n;
    return [...realItems.slice(start), ...realItems.slice(0, start)];
  }, [realItems, startOffset]);

  const virtualCount = hasNextPage
    ? offsetItems.length
    : offsetItems.length * loopCount;

  // Virtualizer measures from the actual scroll element — no manual height needed
  const rowVirtualizer = useVirtualizer({
    count: virtualCount,
    getScrollElement: () => parentRef.current,
    estimateSize: () => (parentRef.current?.clientHeight || (typeof window !== "undefined" ? window.innerHeight : 800)),
    overscan: 1,
  });

  const virtualItems = rowVirtualizer.getVirtualItems();
  const lastVisibleIdx = virtualItems.length
    ? virtualItems[virtualItems.length - 1].index
    : -1;

  // Progress: kartu ke-berapa dari total (untuk indicator bar)
  const currentIdx = virtualItems.length ? virtualItems[0].index : 0;
  const progressPct = virtualCount > 1 ? Math.min(100, (currentIdx / Math.max(1, virtualCount - 1)) * 100) : 0;

  useEffect(() => {
    if (lastVisibleIdx < 0 || !offsetItems.length) return;
    // Mark currently visible items as seen
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const visibleNow = virtualItems.map((vi) => offsetItems[vi.index % offsetItems.length]?.id).filter(Boolean);
    if (visibleNow.length) markSeenBatch(visibleNow);

    if (hasNextPage && !isFetchingNextPage && lastVisibleIdx >= offsetItems.length - 2) {
      fetchNextPage();
      return;
    }
    if (!hasNextPage && lastVisibleIdx >= virtualCount - 2) {
      setLoopCount((m) => Math.min(m + 1, 20));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    lastVisibleIdx,
    offsetItems.length,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    virtualCount,
  ]);

  // Stay on loading screen until data is ready AND virtualizer has items to show
  // This prevents the double-loading flash (data loads → empty render → virtualizer recalculates)
  const hasVirtualItems = virtualItems.length > 0;
  if (isLoading || (!hasVirtualItems && !offsetItems.length)) {
    return (
      <div className="fixed inset-0 grid place-items-center bg-background">
        <div className="text-center">
          <div className="h-10 w-10 rounded-2xl bg-sakura-300 mx-auto opacity-70 animate-pulse" />
          <p className="mt-5 text-plum/60 dark:text-cream/60 text-xs uppercase tracking-[0.3em]">Memuat</p>
        </div>
      </div>
    );
  }

  if (!offsetItems.length) {
    return (
      <div className="fixed inset-0 grid place-items-center bg-background px-6 text-center">
        <div>
          <div className="font-serif italic text-3xl text-plum dark:text-cream mb-2">Belum ada barang</div>
          <p className="text-plum/70 dark:text-cream/70 text-sm">Cek lagi nanti ya 💗</p>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={parentRef}
      className="fixed inset-0 overflow-y-auto snap-y snap-mandatory no-scrollbar bg-cream dark:bg-plum-700"
      data-testid="doomscroll-feed"
      style={{ WebkitOverflowScrolling: "touch" }}
    >
      {/* Progress bar tipis di kanan — micro-interaction posisi feed */}
      <div className="fixed right-1.5 top-1/2 -translate-y-1/2 z-50 h-24 w-1 rounded-full bg-plum/10 dark:bg-cream/15 overflow-hidden pointer-events-none">
        <div
          className="w-full rounded-full bg-sakura-300 transition-all duration-300"
          style={{ height: `${Math.max(12, progressPct)}%` }}
        />
      </div>
      <div style={{ height: `${rowVirtualizer.getTotalSize()}px`, width: "100%", position: "relative" }}>
        {rowVirtualizer.getVirtualItems().map((virtualRow) => {
          const p = offsetItems[virtualRow.index % offsetItems.length];
          if (!p) return null;
          return (
            <div
              key={virtualRow.index}
              data-index={virtualRow.index}
              className="absolute top-0 left-0 w-full snap-start"
              style={{
                height: `${virtualRow.size}px`,
                transform: `translateY(${virtualRow.start}px)`,
              }}
            >
              <ProductCard product={p} onOpenIntent={onOpenIntent} dockStyle={dockStyle} />
            </div>
          );
        })}
      </div>
    </div>
  );
}
